/**
 * Main App Component for XLR8 Admin Console
 *
 * Sets up routing, React Query, and the main application structure
 * for the admin console at /daddyslink route.
 *
 * PERFORMANCE OPTIMIZATION:
 * Uses React.lazy() for code splitting - each route loads only when accessed.
 * This dramatically reduces initial bundle size and improves time-to-interactive.
 */
import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Stripe Provider for payment modals (global so all modals have access)
import { StripeProvider } from "./components/StripeProvider";

// Eager load: Layout component (needed immediately)
import AdminLayout from "./components/layout/AdminLayout";
import AdminPasswordProtection from "./components/auth/AdminPasswordProtection";

// Lazy load: Route components (load on demand)
const Landing = lazy(() => import("./pages/Landing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Users = lazy(() => import("./pages/Users"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const BeatRequestForm = lazy(() => import("./pages/BeatRequestForm"));
const DashboardDemo = lazy(() => import("./pages/DashboardDemo"));
const Login = lazy(() => import("./pages/Login"));
const BetaInviteRequest = lazy(() => import("./pages/BetaInviteRequest"));
const Applications = lazy(() => import("./pages/Applications"));
const Feedback = lazy(() => import("./pages/Feedback"));
const OwnershipRecords = lazy(() => import("./pages/OwnershipRecords"));
const PayoutSettings = lazy(() => import("./pages/PayoutSettings"));
const LogoDrip = lazy(() => import("./pages/LogoDrip"));
const GudGang = lazy(() => import("./pages/GudGang"));
const GudGangPasswordProtection = lazy(
  () => import("./components/auth/GudGangPasswordProtection"),
);
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Legal pages
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const BetaDisclaimer = lazy(() => import("./pages/BetaDisclaimer"));

// Loading fallback component for lazy-loaded routes
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
    <div className="text-center">
      {/* XLR8 Logo with pulse animation */}
      <div className="flex justify-center mb-6">
        <div className="relative">
          <img
            src="/Logo-crop.png"
            alt="XLR8 Logo"
            className="w-24 h-24 object-contain animate-pulse"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 opacity-20 rounded-full blur-xl animate-pulse" />
        </div>
      </div>
      {/* Loading text */}
      <div className="text-gray-600 text-sm font-medium animate-pulse">
        Loading...
      </div>
    </div>
  </div>
);

// Create React Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Smart retry: don't retry on 4xx errors (including 429 rate limiting)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx client errors - these won't succeed on retry
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry up to 2 times for server errors (5xx) and network issues
        return failureCount < 2;
      },
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <StripeProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Personalized onboarding routes - MUST be first to avoid conflicts */}
              <Route path="/slinkup/:name" element={<Onboarding />} />

              {/* Login page for logged out users */}
              <Route path="/slinkup/login" element={<Login />} />

              {/* Password reset routes */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />

              {/* Fallback for /slinkup without name parameter */}
              <Route
                path="/slinkup"
                element={
                  <div className="min-h-screen bg-white flex items-center justify-center">
                    <div className="text-center">
                      {/* XLR8 Logo */}
                      <div className="flex justify-center mb-8">
                        <img
                          src="/Logo-crop.png"
                          alt="XLR8 Logo"
                          className="w-32 h-32 object-contain"
                        />
                      </div>

                      <h1 className="text-2xl font-bold text-black mb-4">
                        Link expired or invalid.
                      </h1>
                      <p className="text-gray-600 mb-4">
                        Hit up Slink if you need a fresh invite.
                      </p>
                      <a
                        href="/slinkup/login"
                        className="text-black underline hover:text-gray-800 transition-colors"
                      >
                        Go to login page
                      </a>
                    </div>
                  </div>
                }
              />

              {/* Legal Pages - Accessible without authentication */}
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/beta-disclaimer" element={<BetaDisclaimer />} />

              {/* Beta Invite Request Form */}
              <Route path="/beta-invite" element={<BetaInviteRequest />} />

              {/* Logo Drip - Floating logo with Vanta.js background */}
              <Route path="/logodrip" element={<LogoDrip />} />

              {/* Beat Request Form - Post-onboarding */}
              <Route path="/beat-request" element={<BeatRequestForm />} />

              {/* User Dashboard - Main application */}
              <Route path="/dashboard/*" element={<DashboardDemo />} />

              {/* Landing page at root - redirects to dashboard if logged in */}
              <Route path="/" element={<Landing />} />

              {/* GudGang routes (for Kayo & Wiz) */}
              <Route
                path="/gudgang"
                element={
                  <GudGangPasswordProtection>
                    <GudGang />
                  </GudGangPasswordProtection>
                }
              />

              {/* Admin console routes - password protection wraps layout to prevent hooks from running before auth */}
              <Route
                path="/daddyslink/*"
                element={
                  <AdminPasswordProtection>
                    <AdminLayout />
                  </AdminPasswordProtection>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="users" element={<Users />} />
                <Route path="applications" element={<Applications />} />
                <Route path="feedback" element={<Feedback />} />
                <Route
                  path="ownership-records"
                  element={<OwnershipRecords />}
                />
                <Route path="payout-settings" element={<PayoutSettings />} />
                <Route
                  path="activities"
                  element={<div>Activities Page (Coming Soon)</div>}
                />
                <Route
                  path="settings"
                  element={<div>Settings Page (Coming Soon)</div>}
                />
              </Route>

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/daddyslink" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </QueryClientProvider>
    </StripeProvider>
  );
}

export default App;
